import {Routes} from '@angular/router';
import {authGuard} from "./services/auth/auth.guard";
import {OverviewComponent} from "./pages/overview/overview/overview.component";
import {Paths} from "./paths";
import {LegalService} from "./services/legal.service";
import {formStateDeactivateGuard} from "./base/deactivate-guard/form-state-deactivate.guard";
import {supportListGuard} from "./pages/support/support.guard";
import {stateReadyGuard} from "./services/state/state-ready.guard";

export const routes: Routes = [
  {path: 'auth/callback', redirectTo: ''},
  {
    path: '',
    canActivate: [authGuard],
    canActivateChild: [authGuard, stateReadyGuard],
    children: [
      {path: '', redirectTo: Paths.OVERVIEW, pathMatch: 'full'},
      // ######################################################################
      // #### Overview
      // ######################################################################
      {path: Paths.OVERVIEW, component: OverviewComponent},
      // ######################################################################
      // #### Legal
      // ######################################################################
      // Paragraph 5 DDG: staendig verfuegbar - der Verweis steht deshalb in der Fusszeile, nicht in einem Menue.
      {
        path: Paths.LEGAL_NOTICE,
        loadComponent: () => import('./pages/legal/legal.component').then((m) => m.LegalComponent),
        data: {document: LegalService.LEGAL_NOTICE}
      },
      {
        path: Paths.LEGAL_OVERVIEW,
        loadComponent: () => import('./pages/legal/legal-overview/legal-overview.component').then((m) => m.LegalOverviewComponent)
      },
      {
        // Der Abschluss des AVV durch einen Bestandskunden. Kein Route-Guard: Der AVV-Fall sperrt niemanden
        // (Entscheidung 2026-08-27), und wer nicht zeichnen darf, bekommt vom Backend eine Absage - das ist
        // die verbindliche Stelle, ein Guard hier waere nur Bedienkomfort.
        path: Paths.AGREEMENT,
        loadComponent: () => import('./pages/agreement/agreement.component').then((m) => m.AgreementComponent)
      },
      {
        path: Paths.LEGAL_TERMS,
        loadComponent: () => import('./pages/legal/legal.component').then((m) => m.LegalComponent),
        data: {document: LegalService.TERMS_OF_USE}
      },
      {
        path: Paths.LEGAL_PRIVACY,
        loadComponent: () => import('./pages/legal/legal.component').then((m) => m.LegalComponent),
        data: {document: LegalService.PRIVACY_POLICY}
      },
      {
        path: Paths.LEGAL_DPA,
        loadComponent: () => import('./pages/legal/legal.component').then((m) => m.LegalComponent),
        data: {document: LegalService.DPA}
      },
      {
        path: Paths.LEGAL_DPA_ANNEX_1,
        loadComponent: () => import('./pages/legal/legal.component').then((m) => m.LegalComponent),
        data: {document: LegalService.SECURITY_MEASURES}
      },
      {
        path: Paths.LEGAL_DPA_ANNEX_2,
        loadComponent: () => import('./pages/legal/legal.component').then((m) => m.LegalComponent),
        data: {document: LegalService.SUBPROCESSORS}
      },
      {
        path: Paths.LEGAL_SCC,
        loadComponent: () => import('./pages/legal/legal.component').then((m) => m.LegalComponent),
        data: {document: LegalService.SCC}
      },
      // ######################################################################
      // #### User
      // ######################################################################
      {
        path: Paths.USER_PROFILE,
        loadComponent: () => import('./pages/user/profile/profile.component').then((m) => m.ProfileComponent),
        canDeactivate: [formStateDeactivateGuard]
      },
      {
        path: Paths.USER_LAYOUT,
        loadComponent: () => import('./pages/user/layout/layout.component').then((m) => m.LayoutComponent),
        canDeactivate: [formStateDeactivateGuard]
      },
      // ######################################################################
      // #### Org
      // ######################################################################
      {
        path: Paths.ORG_LIST,
        loadComponent: () => import('./pages/org/org-list/org-list.component').then((m) => m.OrgListComponent),
        canDeactivate: [formStateDeactivateGuard]
      },
      {
        path: Paths.ORG_ADD,
        loadComponent: () => import('./pages/org/org-add/org-add.component').then((m) => m.OrgAddComponent),
        canDeactivate: [formStateDeactivateGuard]
      },
      {
        path: Paths.ORG_ADD_SUCCESS,
        loadComponent: () => import('./pages/org/org-add-success/org-add-success.component').then((m) => m.OrgAddSuccessComponent),
        canDeactivate: [formStateDeactivateGuard]
      },
      {
        path: Paths.ORG_ADD_ENABLED,
        loadComponent: () => import('./pages/org/org-add-enabled/org-add-enabled.component').then((m) => m.OrgAddEnabledComponent),
        canDeactivate: [formStateDeactivateGuard]
      },
      // ######################################################################
      // #### Contact
      // ######################################################################
      {
        path: Paths.CONTACT_LIST,
        loadComponent: () => import('./pages/contact/contact-list/contact-list.component').then((m) => m.ContactListComponent),
        canDeactivate: [formStateDeactivateGuard]
      },
      {
        path: Paths.CONTACT_VIEW,
        loadComponent: () => import('./pages/contact/contact-details/contact-details.component').then((m) => m.ContactDetailsComponent),
        canDeactivate: [formStateDeactivateGuard]
      },
      {
        path: Paths.CONTACT_EDIT,
        loadComponent: () => import('./pages/contact/contact-details/contact-details.component').then((m) => m.ContactDetailsComponent),
        canDeactivate: [formStateDeactivateGuard]
      },
      {
        path: Paths.CONTACT_RELATION_VIEW,
        loadComponent: () => import('./pages/contact/contact-relation-details/contact-relation-details.component').then((m) => m.ContactRelationDetailsComponent),
        canDeactivate: [formStateDeactivateGuard]
      },
      {
        path: Paths.CONTACT_RELATION_EDIT,
        loadComponent: () => import('./pages/contact/contact-relation-details/contact-relation-details.component').then((m) => m.ContactRelationDetailsComponent),
        canDeactivate: [formStateDeactivateGuard]
      },
      // ######################################################################
      // #### Article
      // ######################################################################
      {
        path: Paths.ARTICLE_LIST,
        loadComponent: () => import('./pages/article/article-list/article-list.component').then((m) => m.ArticleListComponent),
        canDeactivate: [formStateDeactivateGuard]
      },
      {
        path: Paths.ARTICLE_VIEW,
        loadComponent: () => import('./pages/article/article-details/article-details.component').then((m) => m.ArticleDetailsComponent),
        canDeactivate: [formStateDeactivateGuard]
      },
      {
        path: Paths.ARTICLE_EDIT,
        loadComponent: () => import('./pages/article/article-details/article-details.component').then((m) => m.ArticleDetailsComponent),
        canDeactivate: [formStateDeactivateGuard]
      },
      // ######################################################################
      // #### Article - Catalog
      // ######################################################################
      {
        path: Paths.CATALOG_LIST,
        loadComponent: () => import('./pages/catalog/catalog-list/catalog-list.component').then((m) => m.CatalogListComponent),
        canDeactivate: [formStateDeactivateGuard]
      }, {
        path: Paths.CATALOG_VIEW,
        loadComponent: () => import('./pages/catalog/catalog-details/catalog-details.component').then((m) => m.CatalogDetailsComponent),
        canDeactivate: [formStateDeactivateGuard]
      }, {
        path: Paths.CATALOG_EDIT,
        loadComponent: () => import('./pages/catalog/catalog-details/catalog-details.component').then((m) => m.CatalogDetailsComponent),
        canDeactivate: [formStateDeactivateGuard]
      },
      // ######################################################################
      // #### Article - Goods-Group
      // ######################################################################
      {
        path: Paths.GOODS_GROUP_LIST,
        loadComponent: () => import('./pages/article/googs-group-list/googs-group-list.component').then((m) => m.GoogsGroupListComponent),
        canDeactivate: [formStateDeactivateGuard]
      }, {
        path: Paths.GOODS_GROUP_VIEW,
        loadComponent: () => import('./pages/article/goods-group-details/goods-group-details.component').then((m) => m.GoodsGroupDetailsComponent),
        canDeactivate: [formStateDeactivateGuard]
      }, {
        path: Paths.GOODS_GROUP_EDIT,
        loadComponent: () => import('./pages/article/goods-group-details/goods-group-details.component').then((m) => m.GoodsGroupDetailsComponent),
        canDeactivate: [formStateDeactivateGuard]
      },
      // ######################################################################
      // #### Invoice
      // ######################################################################
      {
        path: Paths.INVOICE_LIST,
        loadComponent: () => import('./pages/invoice/invoice-list/invoice-list.component').then((m) => m.InvoiceListComponent),
        canDeactivate: [formStateDeactivateGuard]
      },
      {
        path: Paths.INVOICE_VIEW,
        loadComponent: () => import('./pages/invoice/invoice-details/invoice-details.component').then((m) => m.InvoiceDetailsComponent),
        canDeactivate: [formStateDeactivateGuard]
      },
      {
        path: Paths.INVOICE_EDIT,
        loadComponent: () => import('./pages/invoice/invoice-details/invoice-details.component').then((m) => m.InvoiceDetailsComponent),
        canDeactivate: [formStateDeactivateGuard]
      },
      // ######################################################################
      // #### Receipt
      // ######################################################################
      {
        path: Paths.RECEIPT_LIST,
        loadComponent: () => import('./pages/receipt/receipt-list/receipt-list.component').then((m) => m.ReceiptListComponent),
        canDeactivate: [formStateDeactivateGuard]
      },
      {
        path: Paths.RECEIPT_VIEW,
        loadComponent: () => import('./pages/receipt/receipt-details/receipt-details.component').then((m) => m.ReceiptDetailsComponent),
        canDeactivate: [formStateDeactivateGuard]
      },
      {
        path: Paths.RECEIPT_EDIT,
        loadComponent: () => import('./pages/receipt/receipt-details/receipt-details.component').then((m) => m.ReceiptDetailsComponent),
        canDeactivate: [formStateDeactivateGuard]
      },
      // ######################################################################
      // #### Statistics
      // ######################################################################
      {
        path: Paths.STATISTICS,
        loadComponent: () => import('./pages/statistic/statistic-overview/statistic-overview.component').then((m) => m.StatisticOverviewComponent),
        canDeactivate: [formStateDeactivateGuard]
      },
      // ######################################################################
      // #### Exports
      // ######################################################################
      {
        path: Paths.EXPORTS,
        loadComponent: () => import('./pages/export/export-select/export-select.component').then((m) => m.ExportSelectComponent),
        canDeactivate: [formStateDeactivateGuard]
      },
      // ######################################################################
      // #### Features
      // ######################################################################
      {
        path: Paths.FEATURE_LIST,
        loadComponent: () => import('./pages/feature/feature-list/feature-list.component').then((m) => m.FeatureListComponent),
        canDeactivate: [formStateDeactivateGuard]
      },
      {
        path: Paths.CORRESPONDENCE_LIST,
        loadComponent: () => import('./pages/correspondence/correspondence-list/correspondence-list.component').then((m) => m.CorrespondenceListComponent),
        canDeactivate: [formStateDeactivateGuard]
      },
      {
        path: Paths.MEDIA_LIST,
        loadComponent: () => import('./pages/media/media-list/media-list.component').then((m) => m.MediaListComponent),
        canDeactivate: [formStateDeactivateGuard]
      },
      // ######################################################################
      // #### Support
      // ######################################################################
      {
        path: Paths.SUPPORT_LIST,
        loadComponent: () => import('./pages/support/support-ticket-list/support-ticket-list.component').then((m) => m.SupportTicketListComponent),
        canActivate: [supportListGuard],
        canDeactivate: [formStateDeactivateGuard]
      },
      {
        path: Paths.SUPPORT_VIEW,
        loadComponent: () => import('./pages/support/support-ticket-details/support-ticket-details.component').then((m) => m.SupportTicketDetailsComponent),
        canActivate: [supportListGuard],
        canDeactivate: [formStateDeactivateGuard]
      },
      // ######################################################################
      // #### Org Wallet
      // ######################################################################
      {
        path: Paths.ORG_WALLET_TRANSACTION_LIST,
        loadComponent: () => import('./pages/org-wallet/transaction-list/transaction-list.component').then((m) => m.TransactionListComponent),
        canDeactivate: [formStateDeactivateGuard]
      },
      {
        path: Paths.ORG_WALLET_PAYMENT_CALLBACK,
        loadComponent: () => import('./pages/org-wallet/payment-callback/payment-callback.component').then((m) => m.PaymentCallbackComponent),
        canDeactivate: [formStateDeactivateGuard]
      },
      // ######################################################################
      // #### Settings
      // ######################################################################
      {
        path: Paths.SETTINGS,
        loadComponent: () => import('./pages/settings/settings/settings.component').then((m) => m.SettingsComponent),
        canDeactivate: [formStateDeactivateGuard]
      }, {
        path: Paths.SETTINGS_ORG,
        loadComponent: () => import('./pages/settings/settings-org/settings-org.component').then((m) => m.SettingsOrgComponent),
        canDeactivate: [formStateDeactivateGuard]
      }, {
        path: Paths.SETTINGS_INVOICE,
        loadComponent: () => import('./pages/settings/settings-invoice/settings-invoice.component').then((m) => m.SettingsInvoiceComponent),
        canDeactivate: [formStateDeactivateGuard]
      }, {
        path: Paths.SETTINGS_VAT_RATE,
        loadComponent: () => import('./pages/settings/settings-vat-rate/settings-vat-rate.component').then((m) => m.SettingsVatRateComponent),
        canDeactivate: [formStateDeactivateGuard]
      }, {
        path: Paths.SETTINGS_CONTACT_VEHICLE,
        loadComponent: () => import('./pages/settings/settings-contact-vehicle/settings-contact-vehicle.component').then((m) => m.SettingsContactVehicleComponent),
        canDeactivate: [formStateDeactivateGuard]
      }, {
        path: Paths.SETTINGS_NUMBERS,
        loadComponent: () => import('./pages/settings/settings-numbers/settings-numbers.component').then((m) => m.SettingsNumbersComponent),
        canDeactivate: [formStateDeactivateGuard]
      },
      // ######################################################################
      // #### Settings - Text-Templates
      // ######################################################################
      {
        path: Paths.TEXT_TEMPLATE_LIST,
        loadComponent: () => import('./pages/settings/text-template/text-template-list/text-template-list.component').then((m) => m.TextTemplateListComponent),
        canDeactivate: [formStateDeactivateGuard]
      }, {
        path: Paths.TEXT_TEMPLATE_VIEW,
        loadComponent: () => import('./pages/settings/text-template/text-template-details/text-template-details.component').then((m) => m.TextTemplateDetailsComponent),
        canDeactivate: [formStateDeactivateGuard]
      }, {
        path: Paths.TEXT_TEMPLATE_EDIT,
        loadComponent: () => import('./pages/settings/text-template/text-template-details/text-template-details.component').then((m) => m.TextTemplateDetailsComponent),
        canDeactivate: [formStateDeactivateGuard]
      },
      // ######################################################################
      // #### Settings - User and Groups
      // ######################################################################
      {
        path: Paths.SETTINGS_USER_LIST,
        loadComponent: () => import('./pages/settings/settings-user/settings-user-list/settings-user-list.component').then((m) => m.SettingsUserListComponent),
        canDeactivate: [formStateDeactivateGuard]
      }, {
        path: Paths.SETTINGS_USER_VIEW,
        loadComponent: () => import('./pages/settings/settings-user/settings-user-details/settings-user-details.component').then((m) => m.SettingsUserDetailsComponent),
        canDeactivate: [formStateDeactivateGuard]
      }, {
        path: Paths.SETTINGS_USER_EDIT,
        loadComponent: () => import('./pages/settings/settings-user/settings-user-details/settings-user-details.component').then((m) => m.SettingsUserDetailsComponent),
        canDeactivate: [formStateDeactivateGuard]
      }, {
        path: Paths.SETTINGS_GROUP_LIST,
        loadComponent: () => import('./pages/settings/settings-user/settings-group-list/settings-group-list.component').then((m) => m.SettingsGroupListComponent),
        canDeactivate: [formStateDeactivateGuard]
      }, {
        path: Paths.SETTINGS_GROUP_VIEW,
        loadComponent: () => import('./pages/settings/settings-user/settings-group-details/settings-group-details.component').then((m) => m.SettingsGroupDetailsComponent),
        canDeactivate: [formStateDeactivateGuard]
      }, {
        path: Paths.SETTINGS_GROUP_EDIT,
        loadComponent: () => import('./pages/settings/settings-user/settings-group-details/settings-group-details.component').then((m) => m.SettingsGroupDetailsComponent),
        canDeactivate: [formStateDeactivateGuard]
      },
      // ######################################################################
      // #### Settings - Export
      // ######################################################################
      {
        path: Paths.SETTINGS_EXPORT,
        loadComponent: () => import('./pages/settings/settings-export/settings-export/settings-export.component').then((m) => m.SettingsExportComponent),
        canDeactivate: [formStateDeactivateGuard]
      }, {
        path: Paths.SETTINGS_EXPORT_DATEV,
        loadComponent: () => import('./pages/settings/settings-export/settings-export-datev/settings-export-datev.component').then((m) => m.SettingsExportDatevComponent),
        canDeactivate: [formStateDeactivateGuard]
      },
      // ######################################################################
      // #### Contact-Vehicle (Feature)
      // ######################################################################
      {
        path: Paths.CONTACT_VEHICLE_LIST,
        loadComponent: () => import('./pages/contact-vehicle/contact-vehicle-list/contact-vehicle-list.component').then((m) => m.ContactVehicleListComponent),
        canDeactivate: [formStateDeactivateGuard]
      }, {
        path: Paths.CONTACT_VEHICLE_VIEW,
        loadComponent: () => import('./pages/contact-vehicle/contact-vehicle-details/contact-vehicle-details.component').then((m) => m.ContactVehicleDetailsComponent),
        canDeactivate: [formStateDeactivateGuard]
      }, {
        path: Paths.CONTACT_VEHICLE_EDIT,
        loadComponent: () => import('./pages/contact-vehicle/contact-vehicle-details/contact-vehicle-details.component').then((m) => m.ContactVehicleDetailsComponent),
        canDeactivate: [formStateDeactivateGuard]
      },
      {
        path: Paths.CONTACT_VEHICLE_INSPECTION,
        loadComponent: () => import('./pages/contact-vehicle/contact-vehicle-list/contact-vehicle-list.component').then((m) => m.ContactVehicleListComponent),
        canDeactivate: [formStateDeactivateGuard]
      },
      {
        path: Paths.CONTACT_VEHICLE_TIRES,
        loadComponent: () => import('./pages/contact-vehicle/contact-vehicle-list/contact-vehicle-list.component').then((m) => m.ContactVehicleListComponent),
        canDeactivate: [formStateDeactivateGuard]
      },
      // ######################################################################
      // #### Vehicle (Feature)
      // ######################################################################
      {
        path: Paths.VEHICLE_HISTORY_VIEW,
        loadComponent: () => import('./pages/contact-vehicle/vehicle-history-details/vehicle-history-details.component').then((m) => m.VehicleHistoryDetailsComponent),
        canDeactivate: [formStateDeactivateGuard]
      },
      {
        path: Paths.VEHICLE_HISTORY_EDIT,
        loadComponent: () => import('./pages/contact-vehicle/vehicle-history-details/vehicle-history-details.component').then((m) => m.VehicleHistoryDetailsComponent),
        canDeactivate: [formStateDeactivateGuard]
      },
      {
        path: Paths.VEHICLE_REPAIR_VIEW,
        loadComponent: () => import('./pages/contact-vehicle/vehicle-repair-details/vehicle-repair-details.component').then((m) => m.VehicleRepairDetailsComponent),
        canDeactivate: [formStateDeactivateGuard]
      },
      {
        path: Paths.VEHICLE_REPAIR_EDIT,
        loadComponent: () => import('./pages/contact-vehicle/vehicle-repair-details/vehicle-repair-details.component').then((m) => m.VehicleRepairDetailsComponent),
        canDeactivate: [formStateDeactivateGuard]
      },
      {
        path: Paths.VEHICLE_TIRE_VIEW,
        loadComponent: () => import('./pages/contact-vehicle/vehicle-tire-details/vehicle-tire-details.component').then((m) => m.VehicleTireDetailsComponent),
        canDeactivate: [formStateDeactivateGuard]
      },
      {
        path: Paths.VEHICLE_TIRE_EDIT,
        loadComponent: () => import('./pages/contact-vehicle/vehicle-tire-details/vehicle-tire-details.component').then((m) => m.VehicleTireDetailsComponent),
        canDeactivate: [formStateDeactivateGuard]
      },
      // ######################################################################
      // #### Funeral
      // ######################################################################
      {
        path: Paths.FUNERAL_LIST,
        loadComponent: () => import('./pages/funeral/death-list/death-list.component').then((m) => m.DeathListComponent),
        canDeactivate: [formStateDeactivateGuard]
      }, {
        path: Paths.FUNERAL_VIEW,
        loadComponent: () => import('./pages/funeral/death-details/death-details.component').then((m) => m.DeathDetailsComponent),
        canDeactivate: [formStateDeactivateGuard]
      }, {
        path: Paths.FUNERAL_EDIT,
        loadComponent: () => import('./pages/funeral/death-details/death-details.component').then((m) => m.DeathDetailsComponent),
        canDeactivate: [formStateDeactivateGuard]
      },
      // ######################################################################
      // #### RegistryOffice
      // ######################################################################
      {
        path: Paths.REGISTRY_OFFICE_LIST,
        loadComponent: () => import('./pages/funeral/registry-office-list/registry-office-list.component').then((m) => m.RegistryOfficeListComponent),
        canDeactivate: [formStateDeactivateGuard]
      }, {
        path: Paths.REGISTRY_OFFICE_VIEW,
        loadComponent: () => import('./pages/funeral/registry-office-details/registry-office-details.component').then((m) => m.RegistryOfficeDetailsComponent),
        canDeactivate: [formStateDeactivateGuard]
      }, {
        path: Paths.REGISTRY_OFFICE_EDIT,
        loadComponent: () => import('./pages/funeral/registry-office-details/registry-office-details.component').then((m) => m.RegistryOfficeDetailsComponent),
        canDeactivate: [formStateDeactivateGuard]
      },
      // ######################################################################
      // #### Calculation
      // ######################################################################
      {
        path: Paths.CALC_LYING_ROUND_TIMBER,
        loadComponent: () => import('./pages/calculation/calc-lying-round-timber/calc-lying-round-timber.component').then((m) => m.CalcLyingRoundTimberComponent),
        canDeactivate: [formStateDeactivateGuard]
      },
      // ######################################################################
      // #### Affiliate
      // ######################################################################
      {
        path: Paths.AFFILIATE,
        loadComponent: () => import('./pages/affiliate/affiliate-overview/affiliate-overview.component').then((m) => m.AffiliateOverviewComponent),
        canDeactivate: [formStateDeactivateGuard]
      },
      // ######################################################################
      // #### Sheets / Briefe / Documents / Anschreiben
      // ######################################################################
      {
        path: Paths.SHEET_LIST,
        loadComponent: () => import('./pages/sheet/sheet-list/sheet-list.component').then((m) => m.SheetListComponent),
        canDeactivate: [formStateDeactivateGuard]
      }, {
        path: Paths.SHEET_VIEW,
        loadComponent: () => import('./pages/sheet/sheet-details/sheet-details.component').then((m) => m.SheetDetailsComponent),
        canDeactivate: [formStateDeactivateGuard]
      }, {
        path: Paths.SHEET_EDIT,
        loadComponent: () => import('./pages/sheet/sheet-details/sheet-details.component').then((m) => m.SheetDetailsComponent),
        canDeactivate: [formStateDeactivateGuard]
      },
    ]
  },
  {path: '**', redirectTo: '/' + Paths.OVERVIEW, pathMatch: 'full'}
];
